import './sidebar.scss';
import React, { useEffect, useState } from 'react';
import 'antd/dist/antd.css';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { Storage } from 'react-jhipster';
import { Layout, Menu } from 'antd';
import axios from 'axios';
import {
  AccountBookOutlined,
  DatabaseOutlined,
  BookOutlined,
  CalendarOutlined,
  CarryOutOutlined,
  CloudUploadOutlined,
  CreditCardOutlined,
  DollarCircleOutlined,
  FieldTimeOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  HomeOutlined,
  MessageOutlined,
  PieChartOutlined,
  PictureOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  StopOutlined,
  SyncOutlined,
  TeamOutlined,
  ToolOutlined,
  UserOutlined,
  UserSwitchOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { NavLink as Link, useLocation } from 'react-router-dom';
import { getEcole } from 'app/entities/ecole/ecole.reducer';

const { Sider } = Layout;
const { SubMenu } = Menu;
const PEDAGOGIE_MENU_ENABLED = 'PEDAGOGIE_MENU_ENABLED';
const PEDAGOGIE_ENABLED_LEGACY = 'PEDAGOGIE_ENABLED';

const MENU_PARENT_BY_ITEM: Record<string, string> = {
  'config-student-profiles': 'references',
  'config-classes': 'references',
  'school-parents': 'references',
  personnel: 'references',
  'pedagogy-subjects': 'references',
  'pedagogy-cycles': 'references',
  'school-students': 'school-year',
  'school-planning': 'school-year',
  'school-exemptions': 'school-year',
  'pedagogy-periods': 'school-year',
  'pedagogy-evaluations': 'school-year',
  'pedagogy-report-cards': 'school-year',
  'finance-overview': 'finances',
  'finance-invoices': 'finances',
  'finance-transactions': 'finances',
  'finance-salaries': 'finances',
  'finance-procurement': 'finances',
  'finance-withdrawals': 'finances',
  messages: 'communication',
  'admin-imports': 'administration',
  'admin-school-years': 'administration',
  'admin-report-card-settings': 'administration',
  'admin-school-settings': 'administration',
  'admin-access-control': 'school-year',
};

const getSelectedMenuKey = (pathname: string) => {
  if (pathname.startsWith('/home/')) return 'home';
  if (pathname.startsWith('/eleve/') && pathname.includes('/dispenses')) return 'school-exemptions';
  if (/^\/eleve\/[^/]+$/.test(pathname) || /^\/eleve\/[^/]+\/[^/]+\/details/.test(pathname)) {
    return 'config-student-profiles';
  }
  if (pathname.startsWith('/eleve/')) return 'school-students';
  if (pathname.startsWith('/parent/')) return 'school-parents';
  if (pathname.startsWith('/classe/')) return 'config-classes';
  if (pathname.startsWith('/calendar/')) return 'school-planning';
  if (pathname.startsWith('/matiere-ecole/')) return 'pedagogy-subjects';
  if (pathname.startsWith('/configuration-pedagogie/') || pathname.startsWith('/cycle/')) return 'pedagogy-cycles';
  if (pathname.startsWith('/periode-evaluation/')) return 'pedagogy-periods';
  if (pathname.startsWith('/evaluation/')) return 'pedagogy-evaluations';
  if (pathname.startsWith('/notes/')) return 'pedagogy-report-cards';
  if (pathname.startsWith('/personnel/')) return 'personnel';
  if (pathname.startsWith('/finances/')) return 'finance-overview';
  if (pathname.startsWith('/scolarite/')) return 'finance-invoices';
  if (pathname.startsWith('/transaction/')) return 'finance-transactions';
  if (pathname.startsWith('/salaires/')) return 'finance-salaries';
  if (pathname.startsWith('/approvisonnements/')) return 'finance-procurement';
  if (pathname.startsWith('/virements/')) return 'finance-withdrawals';
  if (pathname.startsWith('/notification/')) return 'messages';
  if (pathname.startsWith('/uploads/')) return 'admin-imports';
  if (pathname.startsWith('/annee-scolaire/')) return 'admin-school-years';
  if (pathname.startsWith('/parametres-bulletin/')) return 'admin-report-card-settings';
  if (pathname.startsWith('/ecole-config/')) return 'admin-school-settings';
  if (pathname.startsWith('/controle-acces')) return 'admin-access-control';
  return undefined;
};

export interface ISidebarProps {
  isAuthenticated: boolean;
  isAdmin: boolean;
  isComptable: boolean;
  isDirecteur: boolean;
  isDirecteurEtude: boolean;
  collapsed: boolean;
  isProfesseur: boolean;
  handleCollapse: any;
}

const Sidebar = (props: ISidebarProps) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const currentEcole = useAppSelector(state => state.ecole.entity);
  const schoolConfigs = useAppSelector(state => state.ecoleConfig.entities);
  const schoolConfigUpdateSuccess = useAppSelector(state => state.ecoleConfig.updateSuccess);
  const storedEcole = Storage.session.get('currentEcole');
  const anneeScolaire = Storage.session.get('currentAnneeScolaire');
  const anneeScolaireActive = useAppSelector(state => state.anneeScolaire.entityActive);
  const { collapsed, handleCollapse, isProfesseur, isAdmin, isComptable, isDirecteur, isDirecteurEtude } = props;
  const [isPedagogieEnabled, setIsPedagogieEnabled] = useState(false);
  const selectedMenuKey = getSelectedMenuKey(location.pathname);
  const activeParentKey = selectedMenuKey ? MENU_PARENT_BY_ITEM[selectedMenuKey] : undefined;
  const [openKeys, setOpenKeys] = useState<string[]>(activeParentKey ? [activeParentKey] : []);
  const ecoleToUse = currentEcole && currentEcole.id ? currentEcole : storedEcole;
  const activeSchoolYear = anneeScolaire || anneeScolaireActive;
  const anneeScolaireId = activeSchoolYear?.id;
  const schoolYearTitle = activeSchoolYear?.nom || 'Année scolaire';
  const canAccessFinances = isComptable || isDirecteur || isAdmin;
  const canManageBulletinBranding = isAdmin || isDirecteur || isDirecteurEtude;
  const canAccessConfiguration = !isProfesseur;
  const canAccessAdministration = !isProfesseur;

  useEffect(() => {
    if ((!currentEcole || !currentEcole.id) && storedEcole?.id) {
      dispatch(getEcole(storedEcole.id));
    }
  }, [currentEcole?.id, storedEcole?.id]);

  useEffect(() => {
    let mounted = true;
    const schoolId = ecoleToUse?.id;

    if (!schoolId) {
      setIsPedagogieEnabled(false);
      return () => {
        mounted = false;
      };
    }

    const pedagogieConfig = schoolConfigs.find(
      config =>
        config?.ecoleId === schoolId && (config?.configKey === PEDAGOGIE_MENU_ENABLED || config?.configKey === PEDAGOGIE_ENABLED_LEGACY)
    );

    if (pedagogieConfig) {
      setIsPedagogieEnabled(pedagogieConfig.value === 'true');
      return () => {
        mounted = false;
      };
    }

    axios
      .get<boolean>(`/api/school-configs/school/${schoolId}/pedagogie-enabled`)
      .then(response => {
        if (mounted) {
          setIsPedagogieEnabled(response.data === true);
        }
      })
      .catch(() => {
        if (mounted) {
          setIsPedagogieEnabled(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [ecoleToUse?.id, location.pathname, schoolConfigUpdateSuccess, schoolConfigs]);

  useEffect(() => {
    if (!collapsed) {
      setOpenKeys(activeParentKey ? [activeParentKey] : []);
    }
  }, [activeParentKey, collapsed, selectedMenuKey]);

  const handleOpenChange = (nextOpenKeys: React.Key[]) => {
    const newlyOpenedKey = nextOpenKeys.find(key => !openKeys.includes(String(key)));
    setOpenKeys(newlyOpenedKey ? [String(newlyOpenedKey)] : []);
  };

  return (
    <Sider
      className="app-sidebar"
      collapsible
      collapsed={collapsed}
      onCollapse={handleCollapse}
      breakpoint="lg"
      width={280}
      hidden={!ecoleToUse || !ecoleToUse.id}
    >
      <Menu
        aria-label="Navigation principale"
        className="sidebar-menu"
        theme="dark"
        mode="inline"
        openKeys={openKeys}
        selectedKeys={selectedMenuKey ? [selectedMenuKey] : []}
        onOpenChange={handleOpenChange}
      >
        <Menu.Item className="sidebar-item" icon={<HomeOutlined />} disabled={isProfesseur} key="home" title="Accueil">
          <Link to={`/home/${ecoleToUse?.id}/${anneeScolaireId}`}>Accueil</Link>
        </Menu.Item>

        {canAccessConfiguration && (
          <SubMenu key="references" icon={<DatabaseOutlined />} title="Référentiels">
            <Menu.Item className="sidebar-item" key="config-student-profiles" icon={<BookOutlined />} title="Fiches élèves">
              <Link to={`/eleve/${ecoleToUse?.id}`}>Fiches élèves</Link>
            </Menu.Item>
            <Menu.Item className="sidebar-item" key="config-classes" icon={<TeamOutlined />} title="Classes">
              <Link to={`/classe/${ecoleToUse?.id}`}>Classes</Link>
            </Menu.Item>
            <Menu.Item className="sidebar-item" key="school-parents" icon={<UserSwitchOutlined />} title="Parents">
              <Link to={`/parent/${ecoleToUse?.id}`}>Parents</Link>
            </Menu.Item>
            <Menu.Item className="sidebar-item" key="personnel" icon={<TeamOutlined />} title="Personnel">
              <Link to={`/personnel/${ecoleToUse?.id}`}>Personnel</Link>
            </Menu.Item>
            <Menu.Item className="sidebar-item" key="pedagogy-subjects" icon={<AccountBookOutlined />} title="Matières">
              <Link to={`/matiere-ecole/${ecoleToUse?.id}`}>Matières</Link>
            </Menu.Item>
            {isPedagogieEnabled && (
              <Menu.Item className="sidebar-item" key="pedagogy-cycles" icon={<BookOutlined />} title="Cycles et niveaux">
                <Link to={`/configuration-pedagogie/${ecoleToUse?.id}`}>Cycles et niveaux</Link>
              </Menu.Item>
            )}
          </SubMenu>
        )}

        <SubMenu key="school-year" icon={<CalendarOutlined />} title={schoolYearTitle}>
          {!isProfesseur && (
            <Menu.Item className="sidebar-item" key="school-students" icon={<UserOutlined />} title="Élèves inscrits">
              <Link to={`/eleve/${ecoleToUse?.id}/${anneeScolaireId}`}>Élèves inscrits</Link>
            </Menu.Item>
          )}
          {(isAdmin || isDirecteur) && anneeScolaireId && (
            <Menu.Item className="sidebar-item" key="admin-access-control" icon={<StopOutlined />} title="Contrôle d’accès">
              <Link to={`/controle-acces/${ecoleToUse?.id}/${anneeScolaireId}`}>Contrôle d’accès</Link>
            </Menu.Item>
          )}
          <Menu.Item className="sidebar-item" key="school-planning" icon={<CalendarOutlined />} title="Planning & évaluations">
            <Link to={`/calendar/${ecoleToUse?.id}/${anneeScolaireId}`}>Planning & évaluations</Link>
          </Menu.Item>
          {!isProfesseur && isPedagogieEnabled && (
            <Menu.Item className="sidebar-item" key="school-exemptions" icon={<StopOutlined />} title="Dispenses">
              <Link to={`/eleve/${ecoleToUse?.id}/${anneeScolaireId}/dispenses`}>Dispenses</Link>
            </Menu.Item>
          )}
          {!isProfesseur && isPedagogieEnabled && (
            <Menu.Item className="sidebar-item" key="pedagogy-periods" icon={<FieldTimeOutlined />} title="Périodes d’évaluation">
              <Link to={`/periode-evaluation/${ecoleToUse?.id}/${anneeScolaireId}`}>Périodes d&apos;évaluation</Link>
            </Menu.Item>
          )}
          {!isProfesseur && isPedagogieEnabled && (
            <Menu.Item className="sidebar-item" key="pedagogy-evaluations" icon={<FileDoneOutlined />} title="Évaluations">
              <Link to={`/evaluation/${ecoleToUse?.id}/${anneeScolaireId}`}>Évaluations</Link>
            </Menu.Item>
          )}
          {!isProfesseur && isPedagogieEnabled && (
            <Menu.Item className="sidebar-item" key="pedagogy-report-cards" icon={<FileTextOutlined />} title="Bulletins">
              <Link to={`/notes/${ecoleToUse?.id}/${anneeScolaireId}/all`}>Bulletins</Link>
            </Menu.Item>
          )}
        </SubMenu>

        {canAccessFinances && (
          <SubMenu key="finances" icon={<WalletOutlined />} title="Finances">
            <Menu.Item className="sidebar-item" key="finance-overview" icon={<PieChartOutlined />} title="Vue d’ensemble">
              <Link to={`/finances/${ecoleToUse?.id}/${anneeScolaireId}`}>Vue d&apos;ensemble</Link>
            </Menu.Item>
            <Menu.Item className="sidebar-item" key="finance-invoices" icon={<FileTextOutlined />} title="Factures">
              <Link to={`/scolarite/${ecoleToUse?.id}/${anneeScolaireId}`}>Factures</Link>
            </Menu.Item>
            <Menu.Item className="sidebar-item" key="finance-transactions" icon={<SyncOutlined />} title="Transactions">
              <Link to={`/transaction/${ecoleToUse?.id}/${anneeScolaireId}`}>Transactions</Link>
            </Menu.Item>
            <Menu.Item className="sidebar-item" key="finance-salaries" icon={<DollarCircleOutlined />} title="Salaires">
              <Link to={`/salaires/${ecoleToUse?.id}/${anneeScolaireId}`}>Salaires</Link>
            </Menu.Item>
            <Menu.Item className="sidebar-item" key="finance-procurement" icon={<ShoppingCartOutlined />} title="Approvisionnements">
              <Link to={`/approvisonnements/${ecoleToUse?.id}/${anneeScolaireId}`}>Approvisionnements</Link>
            </Menu.Item>
            <Menu.Item className="sidebar-item" key="finance-withdrawals" icon={<CreditCardOutlined />} title="Retraits">
              <Link to={`/virements/${ecoleToUse?.id}/${anneeScolaireId}`}>Retraits</Link>
            </Menu.Item>
          </SubMenu>
        )}

        <SubMenu key="communication" icon={<MessageOutlined />} title="Communication">
          <Menu.Item className="sidebar-item" key="messages" icon={<MessageOutlined />} title="Messages">
            <Link to={`/notification/${ecoleToUse?.id}/${anneeScolaireId}`}>Messages</Link>
          </Menu.Item>
        </SubMenu>

        {canAccessAdministration && <Menu.Divider className="sidebar-divider" />}

        {canAccessAdministration && (
          <SubMenu key="administration" icon={<ToolOutlined />} title="Administration">
            <Menu.Item className="sidebar-item" key="admin-imports" icon={<CloudUploadOutlined />} title="Imports">
              <Link to={`/uploads/${ecoleToUse?.id}`}>Imports</Link>
            </Menu.Item>
            {(isAdmin || isDirecteur) && (
              <Menu.Item className="sidebar-item" key="admin-school-years" icon={<CarryOutOutlined />} title="Années scolaires">
                <Link to={`/annee-scolaire/${ecoleToUse?.id}`}>Années scolaires</Link>
              </Menu.Item>
            )}
            {isPedagogieEnabled && canManageBulletinBranding && (
              <Menu.Item
                className="sidebar-item"
                key="admin-report-card-settings"
                icon={<PictureOutlined />}
                title="Paramètres du bulletin"
              >
                <Link to={`/parametres-bulletin/${ecoleToUse?.id}`}>Paramètres du bulletin</Link>
              </Menu.Item>
            )}
            {isAdmin && (
              <Menu.Item className="sidebar-item" key="admin-school-settings" icon={<SettingOutlined />} title="Paramètres de l’école">
                <Link to={`/ecole-config/${ecoleToUse?.id}`}>Paramètres de l&apos;école</Link>
              </Menu.Item>
            )}
          </SubMenu>
        )}
      </Menu>
    </Sider>
  );
};

export default Sidebar;
